import React, { useEffect, useState } from 'react';
import {
    ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
    UploadCloud,
    Activity, Clock3, XCircle, ArrowRight,
    AlertCircle, RefreshCw, BarChart2,
} from 'lucide-react';
import PathologyServices from '../../shared/ApiServices/PathologyServices';
import { PathologyDashboardSkeleton } from '../../shared/custom-ui/skeleton/Pathology';

interface PathologyStatsResponse {
    platform: string;
    dashboard: {
        studiesUploadedToday: number;
        studiesProcessing: number;
        studiesUnderReview: number;
        studiesFailed: number;
    };
    uploads: {
        total: number;
        byUploadStatus: { started: number; completed: number };
        byImportStatus: { notstarted: number; started: number; completed: number; failed: number };
    };
    studies: { totalStudies: number; totalSeries: number };
}

interface KpiCardProps {
    label: string;
    sub: string;
    value: number | string;
    color: string;
    iconBg: string;
    icon: React.ElementType;
    alert?: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, sub, value, color, iconBg, icon: Icon, alert }) => (
    <div
        className={[
            'relative overflow-hidden rounded-[var(--v2-radius-lg)] border p-5',
            'flex flex-col gap-3 transition-[box-shadow,border-color] duration-[var(--v2-duration-normal)]',
            alert ? 'border-red-300' : 'border-[var(--v2-border-glass-strong)]',
        ].join(' ')}
        style={{ boxShadow: 'var(--v2-shadow-card)', background: `linear-gradient(160deg, ${iconBg} 0%, var(--v2-bg-card-inner) 45%)` }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'var(--v2-shadow-card-hover)'; if (!alert) (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-kpi-card-hover-border)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'var(--v2-shadow-card)'; if (!alert) (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-border-glass-strong)'; }}
    >
        <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${color} 0%, ${color}99 100%)` }} />
        <div className="flex items-center justify-between mt-[3px]">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">{label}</span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconBg, border: `1px solid ${color}30` }}>
                <Icon className="w-[15px] h-[15px]" style={{ color }} strokeWidth={2.5} />
            </div>
        </div>
        <p className="text-[28px] font-black tracking-tighter leading-none m-0 tabular-nums" style={{ color }}>
            {value}
        </p>
        <p className="text-[11px] text-[var(--v2-text-muted)] m-0 font-medium">{sub}</p>
    </div>
);

const ChartCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
    title, subtitle, children,
}) => (
    <div className="rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] p-5" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
        <div className="mb-4">
            <h3 className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0 tracking-tight">{title}</h3>
            {subtitle && <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5">{subtitle}</p>}
        </div>
        {children}
    </div>
);

const Dashboard: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<PathologyStatsResponse | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const load = async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const res = await PathologyServices.getPathologyStats() as PathologyStatsResponse;
            setStats(res);
            setLastUpdated(new Date());
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Unable to load pathology metrics');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const timer = setInterval(() => load(true), 90_000);
        return () => clearInterval(timer);
    }, []);

    const uploadTotal = stats?.uploads.total ?? 0;
    const uploadCompleted = stats?.uploads.byUploadStatus.completed ?? 0;
    const uploadStarted = stats?.uploads.byUploadStatus.started ?? 0;
    const uploadPct = uploadTotal > 0 ? Math.round((uploadCompleted / uploadTotal) * 100) : 0;

    const importData = {
        notStarted: stats?.uploads.byImportStatus.notstarted ?? 0,
        started: stats?.uploads.byImportStatus.started ?? 0,
        completed: stats?.uploads.byImportStatus.completed ?? 0,
        failed: stats?.uploads.byImportStatus.failed ?? 0,
    };
    const importPct = uploadTotal > 0
        ? Math.round((importData.completed / uploadTotal) * 100)
        : 0;

    const uploadStatusData = [
        { name: 'Completed', value: uploadCompleted, color: '#16A34A' },
        { name: 'In Progress', value: uploadStarted, color: '#D97706' },
    ];

    const importStatusChartData = [
        { stage: 'Not Started', count: importData.notStarted, color: '#3B82F6' },
        { stage: 'Started', count: importData.started, color: '#D97706' },
        { stage: 'Completed', count: importData.completed, color: '#16A34A' },
        { stage: 'Failed', count: importData.failed, color: '#DC2626' },
    ];

    const pipelineStages = [
        { label: 'Not Started', value: importData.notStarted, color: '#3B82F6', bg: '#EFF6FF' },
        { label: 'In Progress', value: importData.started, color: '#D97706', bg: '#FFFBEB' },
        { label: 'Completed', value: importData.completed, color: '#16A34A', bg: '#F0FDF4' },
        { label: 'Failed', value: importData.failed, color: '#DC2626', bg: '#FEF2F2' },
    ];

    const tooltipStyle = {
        fontSize: 12, borderRadius: 8,
        border: '1px solid var(--v2-border-card)',
        background: 'var(--v2-bg-card)',
        color: 'var(--v2-text-primary)',
    };

    return (
        <div className="v2-animate-fade-in flex flex-col gap-5">

            {loading && <PathologyDashboardSkeleton />}

            {!loading && error && (
                <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                    <div
                        className="w-20 h-20 mb-6 rounded-2xl flex items-center justify-center"
                        style={{ background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.18)' }}
                    >
                        <AlertCircle className="w-9 h-9" style={{ color: '#ef4444' }} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-[18px] font-bold mb-2" style={{ color: 'var(--v2-text-heading)' }}>
                        Failed to load dashboard
                    </h3>
                    <p className="text-[14px] max-w-sm mx-auto mb-6" style={{ color: 'var(--v2-text-muted)' }}>
                        {error}
                    </p>
                    <button
                        onClick={() => load()}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                        style={{ background: '#ef4444', outline: 'none' }}
                    >
                        <RefreshCw className="w-4 h-4" />
                        Try Again
                    </button>
                </div>
            )}

            {!loading && !error && !stats && (
                <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                    <div className="relative mb-7">
                        <div
                            className="w-24 h-24 rounded-3xl flex items-center justify-center"
                            style={{
                                background: 'color-mix(in srgb, var(--v2-primary-500, #3b82f6) 8%, transparent)',
                                border: '1.5px solid color-mix(in srgb, var(--v2-primary-500, #3b82f6) 20%, transparent)',
                            }}
                        >
                            <BarChart2
                                className="w-11 h-11"
                                style={{ color: 'var(--v2-primary-500, #3b82f6)' }}
                                strokeWidth={1.4}
                            />
                        </div>
                        {/* Floating refresh badge */}
                        <div
                            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
                            style={{ background: 'var(--v2-primary-500, #3b82f6)', boxShadow: '0 2px 8px rgba(59,130,246,0.4)' }}
                        >
                            <RefreshCw className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                        </div>
                    </div>
                    <h3 className="text-[20px] font-bold mb-2" style={{ color: 'var(--v2-text-heading)' }}>
                        No metrics available
                    </h3>
                    <p className="text-[14px] max-w-sm mx-auto mb-7 leading-relaxed" style={{ color: 'var(--v2-text-muted)' }}>
                        Pathology dashboard metrics will appear here once studies have been uploaded and processed.
                    </p>
                    <button
                        onClick={() => load()}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                        style={{ background: 'var(--v2-primary-600, #2563eb)', outline: 'none', boxShadow: '0 4px 14px rgba(37,99,235,0.35)' }}
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            )}

            {!loading && !error && stats && (
                <>
                    <div
                        className="grid gap-3"
                        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
                    >
                        <KpiCard
                            label="Uploaded Today"
                            sub={stats.dashboard.studiesUploadedToday === 0
                                ? 'No new studies received today'
                                : 'New studies received today'}
                            value={stats.dashboard.studiesUploadedToday}
                            color="#045EB8"
                            iconBg="rgba(4,94,184,0.10)"
                            icon={UploadCloud}
                        />
                        <KpiCard
                            label="Processing"
                            sub="Studies in import pipeline"
                            value={stats.dashboard.studiesProcessing}
                            color="#D97706"
                            iconBg="rgba(217,119,6,0.10)"
                            icon={Clock3}
                        />
                        <KpiCard
                            label="Under Review"
                            sub="Awaiting pathologist review"
                            value={stats.dashboard.studiesUnderReview}
                            color="#7C3AED"
                            iconBg="rgba(124,58,237,0.10)"
                            icon={Activity}
                        />
                        <KpiCard
                            label="Failed"
                            sub="Import errors requiring action"
                            value={stats.dashboard.studiesFailed}
                            color={stats.dashboard.studiesFailed > 0 ? '#DC2626' : '#0891B2'}
                            iconBg={stats.dashboard.studiesFailed > 0 ? 'rgba(220,38,38,0.10)' : 'rgba(8,145,178,0.10)'}
                            icon={XCircle}
                            alert={stats.dashboard.studiesFailed > 0}
                        />
                    </div>


                    {/* ══ SECTION 2: Import Pipeline (uploads.byImportStatus) ══ */}
                    <div className="rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] p-5" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0 tracking-tight">
                                    Import Pipeline
                                </h3>
                                <p className="text-[11px] text-[var(--v2-text-muted)] m-0 mt-0.5">
                                    {importData.completed} of {uploadTotal} uploads fully imported
                                </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <span className="text-[10px] font-bold uppercase tracking-wider"
                                    style={{ color: importPct >= 80 ? '#16A34A' : 'var(--v2-text-muted)' }}>
                                    {importPct}% complete
                                </span>
                                <div className="w-28 h-1.5 rounded-full bg-[var(--v2-bg-surface)] overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width: `${importPct}%`,
                                            background: importPct >= 80 ? '#16A34A' : '#D97706',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-stretch gap-0">
                            {pipelineStages.map((stage, idx) => (
                                <React.Fragment key={stage.label}>
                                    <div
                                        className="flex-1 rounded-xl p-3.5 flex flex-col gap-1.5 min-w-0"
                                        style={{ background: stage.bg }}
                                    >
                                        <span
                                            className="text-[22px] font-black tabular-nums leading-none"
                                            style={{ color: stage.color }}
                                        >
                                            {stage.value}
                                        </span>
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">
                                            {stage.label}
                                        </span>
                                        <div className="h-1 rounded-full mt-1 bg-white/60 overflow-hidden">
                                            <div
                                                className="h-full rounded-full"
                                                style={{
                                                    width: uploadTotal > 0 ? `${(stage.value / uploadTotal) * 100}%` : '0%',
                                                    background: stage.color,
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {idx < pipelineStages.length - 1 && (
                                        <div className="flex items-center px-1 flex-shrink-0">
                                            <ArrowRight className="w-3.5 h-3.5 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* ══ SECTION 3: Charts — uploads breakdown (visual drill-down) ══ */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                        {/* Upload Status donut with completion % in center */}
                        <ChartCard
                            title="Upload Status"
                            subtitle={`${uploadCompleted} completed · ${uploadStarted} in progress of ${uploadTotal} total`}
                        >
                            <div className="relative h-52">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={uploadStatusData}
                                            dataKey="value"
                                            nameKey="name"
                                            innerRadius={54}
                                            outerRadius={80}
                                            paddingAngle={3}
                                            startAngle={90}
                                            endAngle={-270}
                                        >
                                            {uploadStatusData.map(e => (
                                                <Cell key={e.name} fill={e.color} strokeWidth={0} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip
                                            formatter={(v: number, n: string) => [v, n]}
                                            contentStyle={tooltipStyle}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>

                                {/* Center completion % overlay */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <span
                                        className="text-[20px] font-black tabular-nums leading-none"
                                        style={{ color: uploadPct >= 80 ? '#16A34A' : '#D97706' }}
                                    >
                                        {uploadPct}%
                                    </span>
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] mt-0.5">
                                        done
                                    </span>
                                </div>
                            </div>

                            <div className="flex justify-center gap-5 mt-2">
                                {uploadStatusData.map(d => (
                                    <div key={d.name} className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: d.color }} />
                                        <span className="text-[11px] font-semibold text-[var(--v2-text-secondary)]">
                                            {d.name} ({d.value})
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </ChartCard>

                        {/* Import pipeline per-stage breakdown — per-stage colors */}
                        <ChartCard
                            title="Import Pipeline Breakdown"
                            subtitle="Number of uploads at each import stage"
                        >
                            <div className="h-52">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={importStatusChartData}
                                        margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="var(--v2-border-subtle)"
                                            vertical={false}
                                        />
                                        <XAxis
                                            dataKey="stage"
                                            tick={{ fontSize: 10, fill: 'var(--v2-text-muted)' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            allowDecimals={false}
                                            tick={{ fontSize: 10, fill: 'var(--v2-text-muted)' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <RechartsTooltip
                                            contentStyle={tooltipStyle}
                                            cursor={{ fill: 'var(--v2-bg-hover, rgba(0,0,0,0.03))' }}
                                        />
                                        <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                                            {importStatusChartData.map(entry => (
                                                <Cell key={entry.stage} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </ChartCard>
                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
